/**
 * useNotifications Hook - Phase 5 Epic 5.4
 * Manages real-time notifications with WebSocket integration
 */

import { useState, useEffect, useCallback } from 'react';
import { useWebSocketContext } from '@/contexts/WebSocketContext';
import axios from 'axios';

// Get API base URL - same pattern as client.ts
const getAPIBaseURL = (): string => {
  // In production, always use Railway
  if (import.meta.env.PROD) {
    return 'https://marketing-ai-platform-production.up.railway.app';
  }
  // In development, use environment variable or localhost
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';
};

const API_BASE_URL = getAPIBaseURL();

export type NotificationType =
  | 'agent_execution'
  | 'campaign_update'
  | 'milestone_reached'
  | 'mention'
  | 'system_alert'
  | 'error_alert'
  | 'approval_required'
  | 'recommendation';

export type NotificationPriority = 'critical' | 'high' | 'medium' | 'low';

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  priority: NotificationPriority;
  read: boolean;
  dismissed: boolean;
  entityType?: string;
  entityId?: string;
  actionUrl?: string;
  metadata?: any;
  createdAt: string;
  readAt?: string;
  dismissedAt?: string;
  expiresAt?: string;
}

export interface UseNotificationsOptions {
  userId?: string;
  autoFetch?: boolean;
  unreadOnly?: boolean;
  type?: NotificationType;
  priority?: NotificationPriority;
  limit?: number;
}

export interface UseNotificationsReturn {
  // State
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (notificationId: string) => Promise<void>;
  clearError: () => void;

  // Filters
  getNotificationsByType: (type: NotificationType) => Notification[];
  getNotificationsByPriority: (priority: NotificationPriority) => Notification[];
  getUnreadNotifications: () => Notification[];
  getCriticalNotifications: () => Notification[];
}

export function useNotifications(
  options: UseNotificationsOptions = {}
): UseNotificationsReturn {
  const {
    userId = 'anonymous',
    autoFetch = true,
    unreadOnly = false,
    type,
    priority,
    limit = 50,
  } = options;

  const { socket, isConnected } = useWebSocketContext();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        userId,
        ...(unreadOnly && { unreadOnly: 'true' }),
        ...(type && { type }),
        ...(priority && { priority }),
        ...(limit && { limit: limit.toString() }),
      });

      const response = await axios.get(
        `${API_BASE_URL}/api/v1/notifications?${params.toString()}`
      );

      if (response.data.success) {
        setNotifications(response.data.data);
      } else {
        setError('Failed to fetch notifications');
      }
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.error?.message || 'Failed to fetch notifications');
    } finally {
      setIsLoading(false);
    }
  }, [userId, unreadOnly, type, priority, limit]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/notifications/unread-count?userId=${userId}`
      );

      if (response.data.success) {
        setUnreadCount(response.data.data.count);
      }
    } catch (err: any) {
      console.error('Error fetching unread count:', err);
    }
  }, [userId]);

  // Mark notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        const response = await axios.patch(
          `${API_BASE_URL}/api/v1/notifications/${notificationId}/read`,
          { userId }
        );

        if (response.data.success) {
          // Update local state
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === notificationId
                ? { ...n, read: true, readAt: new Date().toISOString() }
                : n
            )
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      } catch (err: any) {
        console.error('Error marking notification as read:', err);
        setError(err.response?.data?.error?.message || 'Failed to mark as read');
      }
    },
    [userId]
  );

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/notifications/mark-all-read`,
        { userId }
      );

      if (response.data.success) {
        // Update local state
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, read: true, readAt: new Date().toISOString() }))
        );
        setUnreadCount(0);
      }
    } catch (err: any) {
      console.error('Error marking all as read:', err);
      setError(err.response?.data?.error?.message || 'Failed to mark all as read');
    }
  }, [userId]);

  // Dismiss notification
  const dismiss = useCallback(
    async (notificationId: string) => {
      try {
        const response = await axios.delete(
          `${API_BASE_URL}/api/v1/notifications/${notificationId}?userId=${userId}`
        );

        if (response.data.success) {
          // Remove from local state
          setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        }
      } catch (err: any) {
        console.error('Error dismissing notification:', err);
        setError(err.response?.data?.error?.message || 'Failed to dismiss notification');
      }
    },
    [userId]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Filter functions
  const getNotificationsByType = useCallback(
    (filterType: NotificationType) => {
      return notifications.filter((n) => n.type === filterType);
    },
    [notifications]
  );

  const getNotificationsByPriority = useCallback(
    (filterPriority: NotificationPriority) => {
      return notifications.filter((n) => n.priority === filterPriority);
    },
    [notifications]
  );

  const getUnreadNotifications = useCallback(() => {
    return notifications.filter((n) => !n.read);
  }, [notifications]);

  const getCriticalNotifications = useCallback(() => {
    return notifications.filter((n) => n.priority === 'critical' && !n.read);
  }, [notifications]);

  // WebSocket event handlers
  useEffect(() => {
    if (!socket || !isConnected) return;

    // New notification
    const handleNewNotification = (data: any) => {
      setNotifications((prev) => [data, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };

    // Notification marked as read
    const handleNotificationRead = (data: { notificationId: string; readAt: string }) => {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === data.notificationId
            ? { ...n, read: true, readAt: data.readAt }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    };

    // All notifications marked as read
    const handleAllRead = () => {
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
    };

    // Notification dismissed
    const handleNotificationDismissed = (data: { notificationId: string }) => {
      setNotifications((prev) => prev.filter((n) => n.id !== data.notificationId));
    };

    // Register event listeners
    socket.on('notification:new', handleNewNotification);
    socket.on('notification:read', handleNotificationRead);
    socket.on('notification:all_read', handleAllRead);
    socket.on('notification:dismissed', handleNotificationDismissed);

    return () => {
      socket.off('notification:new', handleNewNotification);
      socket.off('notification:read', handleNotificationRead);
      socket.off('notification:all_read', handleAllRead);
      socket.off('notification:dismissed', handleNotificationDismissed);
    };
  }, [socket, isConnected]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [autoFetch, fetchNotifications, fetchUnreadCount]);

  return {
    // State
    notifications,
    unreadCount,
    isLoading,
    error,

    // Actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    dismiss,
    clearError,

    // Filters
    getNotificationsByType,
    getNotificationsByPriority,
    getUnreadNotifications,
    getCriticalNotifications,
  };
}
